import { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export interface MapRetailer {
  id: string;
  name: string;
  type: string;
  address: string | null;
  phone: string | null;
  map_link: string | null;
  latitude: number | null;
  longitude: number | null;
}

// Dhaka center — shown whenever none of the currently-visible retailers have
// coordinates yet, so the map never renders blank/broken.
const DEFAULT_CENTER: [number, number] = [23.8103, 90.4125];
const DEFAULT_ZOOM = 11;

// Leaflet's default marker icon paths break under Vite's bundler (a known
// issue — the images resolve to nowhere), so every marker uses this inline
// SVG pin instead of L.Icon.Default.
const pinIcon = L.divIcon({
  className: '',
  html: `<div style="width:30px;height:30px;transform:translate(-50%,-100%);">
    <svg viewBox="0 0 24 24" width="30" height="30" fill="#7B1113" stroke="white" stroke-width="0.5">
      <path d="M12 0C7.31 0 3.5 3.81 3.5 8.5c0 6.24 7.53 14.6 7.85 14.95a.9.9 0 0 0 1.3 0c.32-.35 7.85-8.71 7.85-14.95C20.5 3.81 16.69 0 12 0zm0 12a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7z"/>
    </svg>
  </div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -28],
});

function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) {
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    } else if (points.length === 1) {
      map.setView(points[0], 15);
    } else {
      map.fitBounds(L.latLngBounds(points), { padding: [32, 32] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(points)]);
  return null;
}

export default function RetailerMap({ retailers }: { retailers: MapRetailer[] }) {
  const pinned = retailers.filter(
    (r): r is MapRetailer & { latitude: number; longitude: number } => r.latitude != null && r.longitude != null,
  );
  const points: [number, number][] = pinned.map((r) => [r.latitude, r.longitude]);

  return (
    <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} scrollWheelZoom={false} style={{ height: '450px', width: '100%' }}>
      {/* Esri's free, no-API-key "World Street Map" — a detailed, colorful
          street-level basemap (roads, water, parks, buildings) closer to
          Google Maps' look than the plain gray canvas used before. Same
          reasons as before for not using raw OSM tiles or CARTO: OSM's
          Mapnik style renders local-script labels (Bengali here), and
          CARTO's equivalent free tiles now require an API key. */}
      <TileLayer
        attribution="Tiles &copy; Esri &mdash; Source: Esri, DeLorme, NAVTEQ, USGS, Intermap, iPC, NRCAN, Esri Japan, METI, Esri China (Hong Kong), Esri (Thailand), TomTom"
        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
        maxZoom={19}
      />
      <FitBounds points={points} />
      {pinned.map((r) => {
        const directionsUrl = r.map_link || `https://www.google.com/maps/dir/?api=1&destination=${r.latitude},${r.longitude}`;
        return (
          <Marker key={r.id} position={[r.latitude, r.longitude]} icon={pinIcon}>
            <Popup maxWidth={240} minWidth={200}>
              <div className="flex flex-col gap-2 py-0.5">
                <div>
                  {/* leaflet.css forces large margins on <p> inside popups —
                      plain divs sidestep that instead of fighting it. */}
                  <div className="text-[13px] font-semibold text-[#2A2A2A] leading-snug">{r.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#8A8A8A] font-medium mt-0.5">{r.type}</div>
                </div>
                {(r.address || r.phone) && (
                  <div className="flex flex-col gap-1.5 pt-2 border-t border-[#2A2A2A]/10">
                    {r.address && (
                      <div className="flex items-start gap-1.5">
                        <i className="ri-map-pin-line text-[#8A8A8A] text-xs mt-0.5 flex-shrink-0"></i>
                        <span className="text-xs text-[#8A8A8A] leading-snug">{r.address}</span>
                      </div>
                    )}
                    {r.phone && (
                      <div className="flex items-center gap-1.5">
                        <i className="ri-phone-line text-[#8A8A8A] text-xs flex-shrink-0"></i>
                        <span className="text-xs text-[#8A8A8A]">{r.phone}</span>
                      </div>
                    )}
                  </div>
                )}
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs !text-[#7B1113] font-semibold pt-1"
                >
                  Get Directions
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </a>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
}
